/*
 * events-archive.js — "Vergangene Events" under the events list (sections/events-list.liquid).
 *
 * The section renders one batch of store pages into <template data-archive-batch>: every
 * page on the event-detail template, with its first and last day. Liquid paginates `pages`
 * by at most 50, so this element fetches the other batches through the Section Rendering
 * API, keeps the events whose last day is before today, sorts them newest first and shows
 * them `data-step` at a time.
 *
 * "Today" is the visitor's date in the shop's time zone, not the server's: the server
 * hides past cards in the upcoming grid too, but a cached render can outlive midnight, so
 * the grid is re-checked here as well.
 */

const EVENTS_TIME_ZONE = 'Europe/Berlin';

/* en-CA formats as YYYY-MM-DD, the same shape as the data attributes, so dates compare
   as strings. */
function eventsToday(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: EVENTS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

class EventsArchive extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.today = eventsToday();
    this.section = this.closest('.events-list');
    this.list = this.querySelector('[data-archive-list]');
    this.button = this.querySelector('[data-archive-more]');
    this.actions = this.querySelector('[data-archive-actions]') || this.button;
    this.status = this.querySelector('[data-archive-status]');
    this.step = Number(this.dataset.step) || 6;
    this.items = null;
    this.shown = 0;

    /* The theme editor shows past cards on purpose (badged), so leave them alone there. */
    if (!(window.Shopify && window.Shopify.designMode)) this.pruneUpcoming();

    if (!this.list || !this.button) return;
    this.button.addEventListener('click', () => this.more());
    this.hidden = false;
  }

  isPast(item) {
    const end = item.dataset.eventEnd;
    return Boolean(end) && end < this.today;
  }

  pruneUpcoming() {
    const grid = this.section && this.section.querySelector('[data-events-upcoming]');
    if (!grid) return;

    for (const item of [...grid.children]) {
      if (this.isPast(item)) item.remove();
    }
    if (grid.children.length === 0) {
      /* Emptied rather than `hidden`: the grid's own `display` would beat [hidden], while
         base.css already hides an empty list. */
      grid.replaceChildren();
      const empty = this.section.querySelector('[data-events-empty]');
      if (empty) empty.hidden = false;
    }
  }

  batchUrl(page) {
    return `${window.location.pathname}?section_id=${encodeURIComponent(this.dataset.sectionId)}&page=${page}`;
  }

  async fetchBatch(page) {
    const response = await fetch(this.batchUrl(page));
    if (!response.ok) throw new Error(`events archive: batch ${page} answered ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const template = doc.querySelector('template[data-archive-batch]');
    if (!template) throw new Error(`events archive: batch ${page} has no archive markup`);
    return template;
  }

  async load() {
    const own = this.querySelector('template[data-archive-batch]');
    const pages = Number(own && own.dataset.pages) || 1;
    const current = Number(own && own.dataset.page) || 1;

    const others = [];
    for (let page = 1; page <= pages; page += 1) {
      if (page !== current) others.push(page);
    }
    const batches = await Promise.all(others.map((page) => this.fetchBatch(page)));

    const seen = new Set();
    const items = [];
    for (const template of [own, ...batches]) {
      if (!template) continue;
      for (const item of template.content.children) {
        const key = item.dataset.eventUrl;
        if (!this.isPast(item) || seen.has(key)) continue;
        seen.add(key);
        items.push(item);
      }
    }

    /* Newest first: by first day, then by last day. */
    return items.sort(
      (a, b) =>
        (b.dataset.eventStart || '').localeCompare(a.dataset.eventStart || '') ||
        (b.dataset.eventEnd || '').localeCompare(a.dataset.eventEnd || '')
    );
  }

  async more() {
    if (this.button.disabled) return;

    if (!this.items) {
      this.setBusy(true);
      try {
        this.items = await this.load();
        this.setStatus('');
      } catch (error) {
        console.error(error);
        this.setStatus(this.dataset.errorLabel);
        return;
      } finally {
        this.setBusy(false);
      }
    }

    const next = this.items.slice(this.shown, this.shown + this.step);
    for (const item of next) this.list.append(document.importNode(item, true));
    this.shown += next.length;

    if (this.shown >= this.items.length) {
      this.actions.hidden = true;
      if (this.items.length === 0) this.setStatus(this.dataset.emptyLabel);
    }
  }

  setBusy(busy) {
    this.button.disabled = busy;
    this.button.setAttribute('aria-busy', String(busy));
  }

  setStatus(text) {
    if (!this.status) return;
    this.status.textContent = text || '';
    this.status.hidden = !text;
  }
}

if (!customElements.get('events-archive')) {
  customElements.define('events-archive', EventsArchive);
}
